/* The single HTTP entry point. Every generated operation in ./generated/client.ts calls `call()`.

   live mode: fetch against the FastAPI backend with the session cookie, the CSRF header on writes,
              RFC 9457 problem-details errors, and a global "session ended" event on 401.
   mock mode: the same operation ids are answered by src/mock with seeded demo data. */
import { OPS } from './generated/operations';

export type CallOptions = {
  path?: Record<string, string>;
  query?: Record<string, unknown>;
  body?: unknown;
  form?: Record<string, unknown>;
  headers?: Record<string, string>;
};

export interface FieldError { field: string; message: string }

export class ApiError extends Error {
  status: number;
  code: string;
  detail: string;
  errors: FieldError[];
  extra: Record<string, unknown>;

  constructor(status: number, code: string, detail: string, errors: FieldError[] = [], extra: Record<string, unknown> = {}) {
    super(detail);
    this.status = status;
    this.code = code;
    this.detail = detail;
    this.errors = errors;
    this.extra = extra;
  }
}

export const API_MODE: 'mock' | 'live' = import.meta.env.VITE_API_MODE === 'live' ? 'live' : 'mock';
const BASE: string = import.meta.env.VITE_API_BASE || '/api/v1';

type MockFn = (opId: string, opts: CallOptions) => Promise<unknown>;
let mockImpl: MockFn | null = null;
/** Installed by src/mock at start-up when VITE_API_MODE=mock. */
export function installMock(fn: MockFn): void {
  mockImpl = fn;
}

/** Every operation id that was called in this browser session (used by the API explorer). */
export const calledOps = new Set<string>();

export function buildPath(template: string, params: Record<string, string> = {}): string {
  return template.replace(/\{(\w+)\}/g, (_, k: string) => {
    const v = params[k];
    if (v === undefined) throw new Error(`missing path parameter ${k} for ${template}`);
    return encodeURIComponent(v);
  });
}

function queryString(query: Record<string, unknown> = {}): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) v.forEach((x) => qs.append(k, String(x)));
    else qs.append(k, String(v));
  }
  const s = qs.toString();
  return s ? `?${s}` : '';
}

function csrfToken(): string | null {
  const m = document.cookie.match(/(?:^|;\s*)hms_csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

export function onSessionEnded(handler: () => void): () => void {
  const fn = () => handler();
  window.addEventListener('hms:session-ended', fn);
  return () => window.removeEventListener('hms:session-ended', fn);
}

export async function call<T>(opId: string, opts: CallOptions = {}): Promise<T> {
  const op = OPS[opId];
  if (!op) throw new Error(`unknown operation ${opId}`);
  calledOps.add(opId);
  if (API_MODE === 'mock') {
    if (!mockImpl) throw new Error('mock API not installed');
    return (await mockImpl(opId, opts)) as T;
  }
  const url = BASE + buildPath(op.path, opts.path) + queryString(opts.query);
  const headers: Record<string, string> = { Accept: 'application/json', ...(opts.headers ?? {}) };
  let body: BodyInit | undefined;
  if (opts.form) {
    const fd = new FormData();
    for (const [k, v] of Object.entries(opts.form)) if (v !== undefined && v !== null) fd.append(k, v as Blob | string);
    body = fd;
  } else if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  if (op.method !== 'GET') {
    const t = csrfToken();
    if (t) headers['X-CSRF-Token'] = t;
  }
  let res: Response;
  try {
    res = await fetch(url, { method: op.method, headers, body, credentials: 'include' });
  } catch {
    throw new ApiError(0, 'NETWORK', 'Cannot reach the server. Check your connection and try again.');
  }
  if (res.status === 401 && opId !== 'auth.me' && opId !== 'auth.login') {
    window.dispatchEvent(new Event('hms:session-ended'));
  }
  if (!res.ok) {
    let problem: Record<string, unknown> = {};
    try {
      problem = await res.json();
    } catch {
      /* not JSON */
    }
    const { status: _s, code, detail, title, errors, type: _t, correlation_id, ...extra } = problem as Record<string, unknown>;
    throw new ApiError(res.status, String(code ?? 'HTTP_ERROR'), String(detail ?? title ?? res.statusText),
      (errors as FieldError[]) ?? [], { ...extra, correlation_id });
  }
  if (op.blob) return (await res.blob()) as T;
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get('content-type') ?? '';
  return (ct.includes('json') ? await res.json() : await res.text()) as T;
}

/** Friendly sentence for any thrown value. */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === 'VERSION_CONFLICT') return 'Someone else changed this record. Reload and try again.';
    return e.detail;
  }
  if (e instanceof Error) return e.message;
  return 'Something went wrong';
}
